import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, InputNumber, Spin, Typography } from 'antd';
// Hooks
import { useGetCbSettings, useSetCbSetting } from 'hooks';
// Types
import {
  GetCbSettings,
  GetCbSettings_getCbSettings_CbInviteProcessingFeeSetting,
} from 'api/cbSettings/types/GetCbSettings';
import { CbSettingScope, CbSettingType } from 'api/graphql-global-types';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './CbSettingsComponent.module.scss';

const { Title, Text } = Typography;

const isInviteProcessingFeeSetting = (
  setting: GetCbSettings['getCbSettings'][number] | undefined
): setting is GetCbSettings_getCbSettings_CbInviteProcessingFeeSetting =>
  !!setting && setting.__typename === 'CbInviteProcessingFeeSetting';

const CbSettingsComponent: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();

  const [inviteFee, setInviteFee] = useState<number | null>(null);
  const [initialInviteFee, setInitialInviteFee] = useState<number | null>(null);

  const { data, loading, error, refetch } = useGetCbSettings({
    scope: CbSettingScope.Agency,
    agencyId: ownerId,
    type: CbSettingType.InviteProcessingFee,
  });

  const [setCbSetting, { loading: saving }] = useSetCbSetting();

  useEffect(() => {
    const setting = data?.getCbSettings?.find(isInviteProcessingFeeSetting);
    const value = setting ? setting.inviteProcessingFee : null;
    setInviteFee(value);
    setInitialInviteFee(value);
  }, [data]);

  const isDirty = inviteFee !== initialInviteFee;
  const canSubmit =
    inviteFee !== null && inviteFee !== undefined && inviteFee >= 0 && isDirty;

  const handleSave = async (): Promise<void> => {
    if (!ownerId || inviteFee === null || inviteFee === undefined) {
      return;
    }

    try {
      await setCbSetting({
        variables: {
          input: {
            type: CbSettingType.InviteProcessingFee,
            inviteProcessingFee: {
              agencyId: ownerId,
              inviteProcessingFee: inviteFee,
            },
          },
        },
      });
      successNotification('Invite processing fee updated successfully!');
      setInitialInviteFee(inviteFee);
      refetch();
    } catch (err: any) {
      errorNotification(
        err?.message || 'Failed to update invite processing fee'
      );
    }
  };

  if (loading && !data) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={`Failed to load CB settings: ${error.message}`}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className={styles.root}>
      <Title level={2}>CB Settings</Title>
      <Card>
        <div className={styles.section}>
          <Title level={4}>Invite Processing Fee</Title>
          <Text type="secondary">
            Per-brand override for the invite processing fee. Leave blank and
            save to keep the global default.
          </Text>

          <div className={styles.card}>
            <div className={styles.formItem}>
              <Text strong className={styles.formItemLabel}>
                Invite processing fee
              </Text>
              <InputNumber
                className={styles.feeInput}
                min={0}
                step={0.1}
                value={inviteFee ?? undefined}
                onChange={(value) =>
                  setInviteFee(typeof value === 'number' ? value : null)
                }
                placeholder="e.g. 5"
              />
              <span className={styles.feeSuffix}>%</span>
            </div>
            <Text type="secondary" className={styles.helperText}>
              Enter a percent value (e.g. 5 for 5%).
            </Text>
          </div>

          <div className={styles.buttonWrapper}>
            <Button
              type="primary"
              loading={saving}
              disabled={!canSubmit}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CbSettingsComponent;
